import { ThemedImg } from '@/components/landingpage/themed-img';
import { TrustBadgesCarousel } from '@/components/landingpage/trust-badges-carousel';
import type { SharedData } from '@/types';
import { useAsset } from '@/utils/asset';
import { usePage } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';

interface SecureProps {
    showBadges?: boolean;
    customDescription1?: string;
    customDescription2?: string;
}

export default function Secure({ showBadges = true, customDescription1, customDescription2 }: SecureProps) {
    const { t } = useTranslation();
    const asset = useAsset();
    const { appearance, colorScheme } = usePage<SharedData>().props;
    const initialDark = colorScheme === 'dark';

    return (
        <div className="bg-linear-to-br from-gray-50 to-white py-12 sm:py-16 lg:py-24 dark:from-gray-950 dark:to-gray-900">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="grid grid-cols-1 items-start gap-x-8 gap-y-16 lg:grid-cols-3">
                    {/* Left Column - Description */}
                    <div className="flex h-full w-full flex-col justify-between text-center lg:col-span-2 lg:text-left">
                        <h2 className="pb-2 text-base/7 font-semibold text-hs-blue dark:text-hs-green-light">{t('home.secure.subtitle')}</h2>
                        <h2 className="pb-4 text-4xl font-semibold tracking-tight text-headline sm:text-5xl">{t('home.secure.title')}</h2>
                        <p className="py-2 text-lg text-paragraph">{customDescription1 ?? t('home.secure.description1')}</p>
                        <p className="py-2 text-lg text-paragraph">{customDescription2 ?? t('home.secure.description2')}</p>

                        {/* Partner Logos */}
                        {showBadges && (
                            <div className="mt-10">
                                {/* Mobile: Auto-scrolling carousel */}
                                <TrustBadgesCarousel mobileOnly />

                                {/* Desktop: Row layout */}
                                <div className="hidden flex-wrap items-center justify-between gap-6 lg:flex">
                                    <div className="group relative flex-1">
                                        <ThemedImg
                                            alt="Google-Bewertungen"
                                            src={asset('images/badges/googleBewertungen.png')}
                                            darkSrc={asset('images/badges/googleBewertungen-dark.png')}
                                            appearance={appearance}
                                            initialDark={initialDark}
                                            className="h-20 w-full object-contain"
                                            loading="lazy"
                                            decoding="async"
                                            height={80}
                                        />
                                    </div>
                                    <div className="group relative flex-1">
                                        <ThemedImg
                                            alt="Konform"
                                            src={asset('images/badges/konform.png')}
                                            darkSrc={asset('images/badges/konform-dark.png')}
                                            appearance={appearance}
                                            initialDark={initialDark}
                                            className="h-20 w-full object-contain"
                                            loading="lazy"
                                            decoding="async"
                                            height={80}
                                        />
                                    </div>
                                    <div className="group relative flex-1">
                                        <img
                                            alt="DSGVO-konform"
                                            src={asset('images/badges/dsgvo.png')}
                                            className="h-20 w-full object-contain dark:brightness-110 dark:contrast-90 dark:grayscale"
                                            loading="lazy"
                                            decoding="async"
                                            height={80}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column - Security Animation */}
                    <div className="mx-auto hidden w-full max-w-xl lg:mx-0 lg:block">
                        <img
                            src={asset('animations/security-on-animate.svg')}
                            alt={t('home.secure.title')}
                            className="h-auto w-full"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
